import { ArrowRight, Play } from "lucide-react";

export default function Hero({ data, brand }) {
  const label = data?.label || brand?.tagline || "Planning Weddings Since 2016";
  const headline = data?.headline || "Your wedding deserves your attention. Not your stress.";
  const subheadline = data?.subheadline || "";
  const primary = data?.primary_cta || { label: "Book a Consultation", href: "#contact" };
  const secondary = data?.secondary_cta || { label: "View Our Work", href: "#gallery" };
  const video = data?.video_url;
  const poster = data?.poster_image || "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=2400&q=80";

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-charcoal text-ivory flex items-end"
    >
      <div className="absolute inset-0">
        {video ? (
          <video
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            poster={poster}
            className="h-full w-full object-cover"
          >
            <source src={video} type="video/mp4" />
          </video>
        ) : (
          <img
            data-testid="hero-poster"
            src={poster}
            alt="Wedding celebration"
            className="h-full w-full object-cover animate-ken-burns"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/35 via-charcoal/45 to-charcoal/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/40 via-transparent to-charcoal/10" />
      </div>

      <div className="relative z-10 w-full">
        <div className="max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24 pt-32 md:pt-40">
          <div className="reveal flex items-center gap-3 mb-7 md:mb-10">
            <span className="h-px w-10 bg-gold/80" />
            <span className="eyebrow text-gold">{label}</span>
          </div>

          <h1
            data-testid="hero-headline"
            className="reveal font-display text-[2.6rem] leading-[1.02] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.4rem] text-ivory max-w-4xl"
          >
            {renderHeadline(headline)}
          </h1>

          {subheadline ? (
            <p
              data-testid="hero-subheadline"
              className="reveal mt-6 md:mt-8 max-w-2xl text-base md:text-lg leading-relaxed font-light text-ivory/85"
            >
              {subheadline}
            </p>
          ) : null}

          <div className="reveal mt-9 md:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a href={primary.href} data-testid="hero-cta-primary" className="btn-gold group">
              {primary.label}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.6} />
            </a>
            <a
              href={secondary.href}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 border border-ivory/40 text-ivory text-[11.5px] uppercase tracking-[0.22em] font-medium hover:bg-ivory hover:text-charcoal transition-colors"
            >
              <Play className="h-3.5 w-3.5" strokeWidth={1.6} />
              {secondary.label}
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 inset-x-0 z-10 hidden md:flex justify-center">
        <div className="flex flex-col items-center gap-2 text-ivory/60">
          <span className="text-[10px] uppercase tracking-[0.35em]">Scroll</span>
          <span className="h-12 w-px bg-ivory/40 animate-pulse" />
        </div>
      </div>
    </section>
  );
}

// Italicise the last sentence after a period for a soft editorial cadence.
function renderHeadline(text) {
  if (!text || typeof text !== "string") return text;
  // If the headline contains two sentences split by ". ", italicise the second
  const sentences = text.split(/\.\s+/);
  if (sentences.length >= 2) {
    const first = sentences.slice(0, -1).join(". ") + ".";
    const last = sentences[sentences.length - 1];
    return (
      <>
        {first}
        <br />
        <em className="italic font-light text-gold not-italic">{last}</em>
      </>
    );
  }
  return text;
}
